import { ComponentType, Overlay } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import { ComponentRef, inject, Injectable } from '@angular/core'
import { outputToObservable } from '@angular/core/rxjs-interop'
import { ArtbookEventNotification, ValentinesDayEventNotification } from '@mos/api/notification/dto'
import {
	AshesAndRosesLaunchEventNotification,
	ChristmasEventNotification,
	HalloweenEventNotification,
	NotificationType,
} from '@mos/api/notification/dto-client'
import { ApiNotificationService } from '@mos/client/web/api-notification/lib/api-notification.service'
import { AbstractModalComponent } from '@mos/client/web/core/components/modal/abstract-modal.component'
import { ModalHistoryRepository } from '@mos/client/web/core/repositories/modal-history.repository'
import { take } from 'rxjs'
import { ArtbookModal } from './artbook/promo/artbook.modal'
import { AshesAndRosesLaunchModal } from './ashes-and-roses/launch/ashes-and-roses-launch.modal'
import { ChristmasPromoModal } from './christmas/promo/christmas-promo.modal'
import { HalloweenPromoModal } from './halloween/promo/halloween-promo.modal'
import { ValentinesDayPromoModal } from './valentines-day/promo/valentines-day-promo.modal'

@Injectable({ providedIn: 'root' })
export class EventService {
	private overlay = inject(Overlay)
	private apiNotificationService = inject(ApiNotificationService)
	private modalHistoryRepository = inject(ModalHistoryRepository)

	constructor() {
		this.apiNotificationService
			.on<HalloweenEventNotification>(NotificationType.EVENT_HALLOWEEN)
			.subscribe((notification) => {
				if (this.modalHistoryRepository.hasModal(HalloweenPromoModal.modalId)) {
					return
				}

				const modalRef = this.openModal<HalloweenPromoModal>(HalloweenPromoModal)
				modalRef.setInput('offers', notification.offers)
				this.modalHistoryRepository.logModal(HalloweenPromoModal.modalId)
			})

		this.apiNotificationService
			.on<ChristmasEventNotification>(NotificationType.EVENT_CHRISTMAS)
			.subscribe((notification) => {
				if (this.modalHistoryRepository.hasModal(ChristmasPromoModal.modalId)) {
					return
				}

				const modalRef = this.openModal<ChristmasPromoModal>(ChristmasPromoModal)
				modalRef.setInput('offers', notification.offers)
				this.modalHistoryRepository.logModal(ChristmasPromoModal.modalId)
			})

		this.apiNotificationService
			.on<ValentinesDayEventNotification>(NotificationType.EVENT_VALENTINES_DAY)
			.subscribe((notification) => {
				if (this.modalHistoryRepository.hasModal(ValentinesDayPromoModal.modalId)) {
					return
				}

				const modalRef = this.openModal<ValentinesDayPromoModal>(ValentinesDayPromoModal)
				modalRef.setInput('offers', notification.offers)
				this.modalHistoryRepository.logModal(ValentinesDayPromoModal.modalId)
			})

		this.apiNotificationService
			.on<ArtbookEventNotification>(NotificationType.ARTBOOK_EVENT)
			.subscribe((notification) => {
				if (this.modalHistoryRepository.hasModal(ArtbookModal.modalId)) {
					return
				}

				const modalRef = this.openModal<ArtbookModal>(ArtbookModal)
				modalRef.setInput('committed', notification.committed)
				modalRef.setInput('goal', notification.goal ?? 100)
				modalRef.setInput('end', notification.end ?? new Date('2025-09-10T21:59:19.2Z'))
				modalRef.setInput('goalRaised', notification.goalRaised ?? false)
				this.modalHistoryRepository.logModal(ArtbookModal.modalId)
			})

		this.apiNotificationService
			.on<AshesAndRosesLaunchEventNotification>(NotificationType.ASHES_AND_ROSES_LAUNCH_EVENT)
			.subscribe(() => {
				if (this.modalHistoryRepository.hasModal(AshesAndRosesLaunchModal.modalId)) {
					return
				}

				this.openModal<AshesAndRosesLaunchModal>(AshesAndRosesLaunchModal)
				this.modalHistoryRepository.logModal(AshesAndRosesLaunchModal.modalId)
			})
	}

	private openModal<T extends AbstractModalComponent>(modalType: ComponentType<T>): ComponentRef<T> {
		const overlayRef = this.overlay.create({
			panelClass: 'memory-of-souls',
			backdropClass: 'memory-of-souls',
		})
		const portal = new ComponentPortal<T>(modalType)
		const component = overlayRef.attach(portal)
		outputToObservable(component.instance.mosClose)
			.pipe(take(1))
			.subscribe(() => {
				overlayRef.dispose()
			})
		component.instance.opened = true

		return component
	}
}
